import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, MousePointerClick } from 'lucide-react';
import {
  getHeatmap,
  getHeatmapPaths,
  lastNDaysRange,
  qk,
  type HeatmapPath,
  type HeatmapQuery,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useActivePid, useProjectContext } from '../components/Layout.js';
import { formatNumber, formatPct } from '../lib/format.js';
import { projectOrigin } from '../lib/project-origin.js';
import {
  cssColorFor,
  HEATMAP_GRID,
  renderHeatPixels,
  scrollReach,
  stageSize,
  type HeatmapDevice,
} from '../lib/heatmap-render.js';

/**
 * §23.5 Isı Haritası — canlı sayfa iframe'i üzerine canvas overlay.
 *
 * Sayfa yalnızca DOM işini yapar: veri çekme (`api.ts`), ölçek/renk/leke matematiği
 * (`lib/heatmap-render.ts`) dışarıdadır. Görsel dil panelin geri kalanıyla birebirdir
 * (Funnel/Oturumlar kabuğu: beyaz başlık şeridi → beyaz filtre şeridi → beyaz
 * istatistik şeridi → #fafafa zeminde px-6 py-8 içerik).
 *
 * §23.5 KARARI: cihaz toggle'ı yalnız desktop/mobile'dır — `tablet` API'de kabul
 * edilir ama trafik payı düşük olduğu için haritası seyrek kalır.
 */

/** §23.5 — iframe yüklenmesini bu süre içinde bildirmezse ızgara fallback'ine düşülür. */
const FRAME_TIMEOUT_MS = 5_000;

/** §23.4 yardımcı uç zaten ilk 20 deseni döner; panel kendi tarafında da sınırlar. */
const MAX_PATHS = 20;

/** §23.5 boş durum metni — golden/e2e bu cümleyi arar. */
const EMPTY_CLICK_TITLE = 'Bu desen/cihaz için henüz tıklama verisi yok';
const EMPTY_SCROLL_TITLE = 'Bu desen/cihaz için henüz scroll verisi yok';
const EMPTY_CLICK_DESCRIPTION = 'Veri M10 yayınından sonra birikir.';

type HeatmapMode = 'click' | 'scroll';

/* -------------------------------------------------------------------------- */
/* Kontroller                                                                  */
/* -------------------------------------------------------------------------- */

/** Funnel'daki `FilterPill` ile aynı açılır seçici — burada desen listesi taşır. */
function PathPicker({
  options,
  value,
  onChange,
  disabled,
}: {
  options: HeatmapPath[];
  value: string | null;
  onChange: (pattern: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="heatmap-path-picker"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-8 max-w-[420px] items-center gap-1.5 whitespace-nowrap rounded-lg border bg-white px-3 text-[13px] text-[#374151] transition-colors disabled:opacity-40 ${
          open ? 'border-brand' : 'border-[#e5e7eb] hover:border-[#d1d5db]'
        }`}
      >
        <span className="truncate font-mono text-[12px]">{value ?? 'Desen seçin'}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[#9ca3af] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-72 min-w-[320px] overflow-y-auto rounded-xl border border-[#e5e7eb] bg-white py-1 shadow-lg">
          {options.map((option) => (
            <button
              key={option.pattern}
              type="button"
              data-testid="heatmap-path-option"
              data-pattern={option.pattern}
              onClick={() => {
                onChange(option.pattern);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors hover:bg-[#f9fafb] ${
                option.pattern === value ? 'font-medium text-brand' : 'text-[#374151]'
              }`}
            >
              <span className="truncate font-mono text-[12px]">{option.pattern}</span>
              <span className="num shrink-0 text-[11px] text-[#9ca3af]">
                {formatNumber(option.views)}
              </span>
            </button>
          ))}
          {options.length === 0 ? (
            <p className="px-3 py-2 text-[13px] text-[#9ca3af]">Görüntülenen desen bulunamadı</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Oturumlar sayfasındaki `FilterTab` dili — aktif olan marka tonunda. */
function Toggle({
  active,
  onClick,
  children,
  testId,
  dataAttr,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
  testId: string;
  dataAttr: Record<string, string>;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      {...dataAttr}
      aria-pressed={active}
      onClick={onClick}
      className={`h-8 whitespace-nowrap rounded-lg px-3 text-[13px] transition-colors ${
        active
          ? 'bg-[#fff4ec] font-medium text-brand'
          : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]'
      }`}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* iframe adresi                                                               */
/* -------------------------------------------------------------------------- */

/**
 * §23.5 arka planı: `projects.domain` + desenin `samplePath`'i.
 *
 * `domain` şemada şemasız da tutulabilir ('demo-shop.local'); http(s) öneki yoksa
 * https varsayılır. Bozuk domain/path'te `null` döner — çağıran ızgara fallback'ine
 * düşer, panel çökmez.
 */
export function buildFrameSrc(
  domain: string | null | undefined,
  samplePath: string | null | undefined,
): string | null {
  if (samplePath === null || samplePath === undefined || samplePath === '') return null;
  // Şema/loopback kuralı `lib/project-origin.ts`tedir — replay `<base href>` ile ortak.
  const origin = projectOrigin(domain);
  if (origin === null) return null;
  try {
    return new URL(samplePath, origin).toString();
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Sayfa                                                                       */
/* -------------------------------------------------------------------------- */

export default function Heatmap() {
  const pid = useActivePid();
  const { projects } = useProjectContext();
  const [pattern, setPattern] = useState<string | null>(null);
  const [device, setDevice] = useState<HeatmapDevice>('desktop');
  const [mode, setMode] = useState<HeatmapMode>('click');
  /** §23.5 — iframe engellendi mi? 'loading' → onLoad gelirse 'ok', gelmezse 'blocked'. */
  const [frameState, setFrameState] = useState<'loading' | 'ok' | 'blocked'>('loading');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // §23.4 varsayılan pencere son 14 gündür; mount'ta sabitlenir ki sorgu anahtarı
  // her render'da değişip gereksiz istek üretmesin.
  const range = useMemo(() => lastNDaysRange(14), []);

  const paths = useQuery({
    queryKey: qk.heatmapPaths(pid),
    queryFn: ({ signal }) => getHeatmapPaths(pid, signal),
  });

  const options = (paths.data?.items ?? []).slice(0, MAX_PATHS);
  /*
   * §33.5 — desen listesi sunucu tavanına (`HEATMAP_PATH_LIMIT`) DEĞDİYSE söylenir.
   * `truncated` okunmuyordu: 20'den çok deseni olan bir sitede kullanıcı, seçicide
   * göremediği bir sayfanın ısı haritası HİÇ YOK sanıyordu (denetim bulgusu B4).
   */
  const desenKirpildi = paths.data?.truncated === true;
  // Seçim yapılmadıysa en çok görüntülenen desen (uç zaten views'e göre sıralı gelir).
  const selected = options.find((o) => o.pattern === pattern) ?? options[0];

  const params: HeatmapQuery | null =
    selected === undefined
      ? null
      : { path: selected.pattern, device, mode, from: range.from, to: range.to };

  const heat = useQuery({
    queryKey: qk.heatmap(pid, params ?? { path: '', device }),
    queryFn: ({ signal }) => getHeatmap(pid, params as HeatmapQuery, signal),
    enabled: params !== null,
  });

  /*
   * §37.4 — arka plan ÖNCELİĞİ: (1) ekran görüntüsü, (2) canlı iframe, (3) ızgara.
   * Screenshot varsa iframe HİÇ kurulmaz: yabancı bir sayfayı panelin içinde
   * çalıştırmamak (script, çerez, üçüncü taraf istek) hem daha hızlı hem daha güvenlidir.
   */
  const background = heat.data?.background ?? null;
  // §37.3 — gerçek yükseklik varsa 3 kat VARSAYIMI kullanılmaz.
  const stage = stageSize(device, background);
  const grid = heat.data?.grid ?? [];
  const bands = useMemo(() => scrollReach(heat.data?.scroll ?? []), [heat.data]);
  const topSelectors = heat.data?.topSelectors ?? [];
  const scrollTotal = (heat.data?.scroll ?? []).reduce((sum, row) => sum + row.c, 0);

  /*
   * §23.5 arka plan adresi — KABUK BAĞLAMINDAN okunur, ayrı bir sorgudan değil.
   *
   * §72.5/4: eskiden bu sayfa global `/api/projects` ucunu çağırıyordu; panelin geri
   * kalanı ise org'a filtreli listeyi kullanıyordu. İki kaynak, birden çok org'a üye
   * bir kullanıcıda ayrışabilir ve ısı haritası BAŞKA bir org'un domain'ini arka
   * plana koyabilirdi. Bağlam zaten aktif org'un projelerini taşıyor: fazladan istek
   * de kalktı, ayrışma İMKÂNSIZ hâle geldi.
   */
  const domain = projects.find((p) => p.id === pid)?.domain ?? null;
  const frameSrc = buildFrameSrc(domain, selected?.samplePath);

  const showEmpty = mode === 'click' ? grid.length === 0 : scrollTotal === 0;
  const busy = heat.isLoading || paths.isLoading;

  /**
   * Çerçeve GERÇEKTEN DOM'da mı? Sahne yalnız veri geldiğinde ve boş durum kartı
   * kapalıyken çizilir; zaman aşımı sayacı ancak o zaman anlamlıdır.
   */
  const frameMounted = background === null && frameSrc !== null && !heat.isLoading && !showEmpty;

  /**
   * §23.5 KARARI — iframe engellenirse (X-Frame-Options/CSP) ızgara fallback'i.
   *
   * Cross-origin bir çerçevenin GERÇEKTEN çizilip çizilmediği okunamaz (contentDocument
   * erişimi her durumda güvenlik hatası verir) ve engellenen çerçeve bazı tarayıcılarda
   * yine `load` tetikler. Bu yüzden tespit şartnamedeki asgari ölçüttür: `onLoad`
   * zamanında gelmezse engellenmiş sayılır. Yanlış pozitifin bedeli yalnızca arka
   * planın ızgaraya düşmesidir — haritanın analitik değeri korunur.
   *
   * Sayaç YALNIZ çerçeve DOM'dayken işler (`frameMounted`): aksi hâlde yavaş bir API
   * yanıtı ya da veri bulunmayan bir desen, hiç yüklenmeye başlamamış bir iframe'i
   * "engellendi" ilan eder; kullanıcı mod/desen değiştirip veriye ulaştığında canlı
   * sayfa yerine kalıcı olarak ızgara fallback'ini görürdü.
   */
  useEffect(() => {
    if (frameSrc === null) {
      setFrameState('blocked');
      return;
    }
    setFrameState('loading');
    if (!frameMounted) return;
    const timer = setTimeout(() => {
      setFrameState((current) => (current === 'loading' ? 'blocked' : current));
    }, FRAME_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [frameSrc, frameMounted]);

  /**
   * Tıklama modu çizimi: piksel tamponu saf çekirdekte üretilir (§21.2 elle gauss),
   * burada yalnızca canvas'a basılır. `getContext` jsdom/ortam desteği yoksa null
   * döner — panel bu durumda sessizce boş canvas ile yaşar.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (grid.length === 0) return;

    const pixels = renderHeatPixels(grid, stage);
    const image = ctx.createImageData(stage.w, stage.h);
    image.data.set(pixels);
    ctx.putImageData(image, 0, 0);
  }, [grid, stage.w, stage.h, mode]);

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="isi-haritasi-sayfasi">
      {/* başlık */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Isı Haritası</h1>
          <BilgiDugmesi testId="heatmap-bilgi">Tıklama yoğunluğu ve scroll erişimi, mevcut click/page_leave olaylarından üretilir (§23.1). Koordinatlar M10 sonrası SAYFA-göreli tabandadır (§23.2); daha eski veri farklı tabandan gelmiş olabilir.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Son 14 gün · {device === 'mobile' ? 'mobil' : 'masaüstü'} · 100×100 ızgara
          </span>
        </div>
      </div>

      {/* filtreler */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[#e5e7eb] bg-white px-6 py-3">
        <PathPicker
          options={options}
          value={selected?.pattern ?? null}
          onChange={setPattern}
          disabled={paths.isLoading || options.length === 0}
        />

        {/* §33.5 — sessiz kırpma yok: liste tavana değdiyse ekran bunu söyler. */}
        {desenKirpildi ? (
          <span
            data-testid="heatmap-desen-kirpildi"
            title={`Sunucu bu uçta en çok ${String(paths.data?.limit ?? MAX_PATHS)} desen döndürür.`}
            className="rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
          >
            En çok görüntülenen {formatNumber(paths.data?.limit ?? MAX_PATHS)} desen listeleniyor
          </span>
        ) : null}

        <div className="flex items-center gap-1" role="group" aria-label="Cihaz">
          <Toggle
            active={device === 'desktop'}
            onClick={() => setDevice('desktop')}
            testId="heatmap-device"
            dataAttr={{ 'data-device': 'desktop' }}
          >
            Masaüstü
          </Toggle>
          <Toggle
            active={device === 'mobile'}
            onClick={() => setDevice('mobile')}
            testId="heatmap-device"
            dataAttr={{ 'data-device': 'mobile' }}
          >
            Mobil
          </Toggle>
        </div>

        <span aria-hidden="true" className="h-5 w-px bg-[#e5e7eb]" />

        <div className="flex items-center gap-1" role="group" aria-label="Mod">
          <Toggle
            active={mode === 'click'}
            onClick={() => setMode('click')}
            testId="heatmap-mode"
            dataAttr={{ 'data-mode': 'click' }}
          >
            Tıklama
          </Toggle>
          <Toggle
            active={mode === 'scroll'}
            onClick={() => setMode('scroll')}
            testId="heatmap-mode"
            dataAttr={{ 'data-mode': 'scroll' }}
          >
            Scroll
          </Toggle>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {busy ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          /*
           * Çipler MODA GÖRE seçilir. API tıklama modunda `scroll: []`, scroll modunda
           * `grid: []` döndürür; hepsini birden basmak "Scroll görüntüleme 0" (veya
           * "Tıklama 0") yazdırır ve bu, VERİ YOK diye okunur — oysa yalnızca o mod
           * sorgulanmamıştır. Yanıltıcı sıfır göstermektense ilgisiz çipi hiç basmıyoruz.
           */
          (mode === 'click'
            ? [
                { label: 'Tıklama', value: formatNumber(heat.data?.totalClicks ?? 0) },
                { label: 'Oturum', value: formatNumber(heat.data?.totalSessions ?? 0) },
                { label: 'Dolu hücre', value: formatNumber(grid.length) },
              ]
            : [
                { label: 'Scroll görüntüleme', value: formatNumber(scrollTotal) },
                { label: 'Oturum', value: formatNumber(heat.data?.totalSessions ?? 0) },
                { label: 'Dilim', value: formatNumber(bands.length) },
              ]
          ).map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{s.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {paths.isError ? (
          <ErrorState
            title="Sayfa desenleri alınamadı"
            description={(paths.error as Error).message}
            onRetry={() => void paths.refetch()}
          />
        ) : null}

        {heat.isError ? (
          <ErrorState
            title="Isı haritası alınamadı"
            description={(heat.error as Error).message}
            onRetry={() => void heat.refetch()}
          />
        ) : null}

        {!paths.isLoading && !paths.isError && options.length === 0 ? (
          <EmptyState
            title="Henüz görüntülenen sayfa deseni yok"
            description="Isı haritası, son 14 günde görüntülenmiş sayfa desenleri üzerinde çalışır."
          />
        ) : null}

        {selected !== undefined ? (
          <Card
            title={mode === 'click' ? 'Tıklama yoğunluğu' : 'Scroll erişimi'}
            subtitle={
              mode === 'click'
                ? `${selected.pattern} · ${stage.w}×${stage.h}px sanal sahne · 12px leke`
                : `${selected.pattern} · 20 dilim · kümülatif erişim`
            }
            actions={
              <span className="font-mono text-[11px] text-[#9ca3af]">{selected.samplePath}</span>
            }
            flush
          >
            {heat.isLoading ? <LoadingBlock label="Isı haritası yükleniyor…" /> : null}

            {!heat.isLoading && showEmpty ? (
              <EmptyState
                compact
                icon={<MousePointerClick className="h-6 w-6 text-[#9ca3af]" />}
                title={mode === 'click' ? EMPTY_CLICK_TITLE : EMPTY_SCROLL_TITLE}
                description={`${EMPTY_CLICK_DESCRIPTION} Farklı bir desen ya da cihaz seçebilirsiniz.`}
              />
            ) : null}

            {!heat.isLoading && !showEmpty ? (
              <>
                {/* §37.4 — screenshot varken bu uyarı GÖSTERİLMEZ: iframe hiç denenmedi. */}
                {background === null && frameState === 'blocked' ? (
                  <div className="border-b border-[#fde68a] bg-[#fffbeb] px-5 py-2.5 text-[12px] text-[#92400e]">
                    Sayfa gömülemedi ({frameSrc === null ? 'proje domaini tanımlı değil' : 'X-Frame-Options / CSP'})
                    — harita boş 100×100 ızgara üzerine çiziliyor.
                  </div>
                ) : null}

                <div className="max-h-[70vh] overflow-auto bg-[#f3f4f6] p-6">
                  <div
                    className="relative mx-auto bg-white shadow-sm"
                    style={{ width: stage.w, height: stage.h }}
                  >
                    {/* §37.4 — (1) ekran görüntüsü, (2) canlı sayfa, (3) ızgara. */}
                    {background !== null ? (
                      <img
                        src={background.url}
                        alt=""
                        data-testid="isi-haritasi-goruntu"
                        /*
                         * `object-fit: fill` bilinçlidir: sahne zaten görüntünün en-boy
                         * oranından türetildi (stageSize), yani ölçek birebirdir. `cover`
                         * kırpar, `contain` boşluk bırakır — ikisi de overlay'i kaydırırdı.
                         */
                        className="absolute inset-0 h-full w-full"
                        style={{ objectFit: 'fill' }}
                      />
                    ) : frameState !== 'blocked' && frameSrc !== null ? (
                      <iframe
                        key={frameSrc}
                        src={frameSrc}
                        title="Isı haritası arka planı"
                        data-testid="isi-haritasi-iframe"
                        // Yabancı bir sayfa panelin içinde açılıyor: üst pencereyi
                        // gezdirmesine ve pop-up açmasına izin YOK. `allow-scripts`
                        // korunur, aksi hâlde çoğu sayfa hiç çizilmez; çerçeve
                        // cross-origin olduğu için panel DOM'una erişemez.
                        sandbox="allow-scripts allow-same-origin"
                        referrerPolicy="no-referrer"
                        onLoad={() => setFrameState('ok')}
                        className="absolute inset-0 h-full w-full border-0"
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        data-testid="isi-haritasi-izgara"
                        className="absolute inset-0"
                        style={{
                          // §23.5 fallback: 100×100 çizgi ızgarası (elle, kütüphanesiz).
                          backgroundImage: `repeating-linear-gradient(to right, #e5e7eb 0 1px, transparent 1px ${stage.w / HEATMAP_GRID}px), repeating-linear-gradient(to bottom, #e5e7eb 0 1px, transparent 1px ${stage.h / HEATMAP_GRID}px)`,
                        }}
                      />
                    )}

                    {/* §23.5 — overlay canvas iframe'in ÜSTÜNDE, pointer-events yok. */}
                    {mode === 'click' ? (
                      <canvas
                        ref={canvasRef}
                        data-testid="isi-haritasi-canvas"
                        width={stage.w}
                        height={stage.h}
                        className="absolute inset-0"
                        style={{
                          width: stage.w,
                          height: stage.h,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null}

                    {/* §23.5 scroll modu — 20 yatay bant + sağda erişim etiketi. */}
                    {mode === 'scroll' ? (
                      <div
                        data-testid="isi-haritasi-scroll"
                        className="absolute inset-0"
                        style={{ pointerEvents: 'none' }}
                      >
                        {bands.map((band) => (
                          <div
                            key={band.bucket}
                            data-testid="isi-haritasi-scroll-bandi"
                            data-bucket={band.bucket}
                            className="absolute left-0 right-0 flex items-center justify-end border-b border-white/40"
                            style={{
                              top: `${(band.bucket * 100) / bands.length}%`,
                              height: `${100 / bands.length}%`,
                              background: cssColorFor(band.pct / 100),
                            }}
                          >
                            <span className="mr-3 rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-[#111827] shadow-sm">
                              {formatPct(band.pct, 0)} kullanıcı buraya ulaştı
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </Card>
        ) : null}

        {/* §23.4 topSelectors — "nerede yığılıyor" sorusunun metin karşılığı (§23.1). */}
        {selected !== undefined && !heat.isLoading ? (
          <Card
            title="En çok tıklanan selector'lar"
            subtitle="Oturum sayısına göre ilk 10 — canlı sorgu (§23.4)"
            flush
          >
            <ol data-testid="top-selectors" className="divide-y divide-[#f3f4f6]">
              {topSelectors.map((row, index) => (
                <li
                  key={`${row.selector}-${index}`}
                  className="flex items-center gap-3 px-5 py-2.5 text-[13px]"
                >
                  <span className="num w-5 shrink-0 text-[12px] text-[#9ca3af]">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[#111827]">
                    {row.selector}
                  </span>
                  <span className="num shrink-0 text-[#374151]">
                    {formatNumber(row.sessions)} oturum
                  </span>
                  <span className="num w-28 shrink-0 text-right text-[#9ca3af]">
                    {formatNumber(row.events)} tıklama
                  </span>
                </li>
              ))}
              {topSelectors.length === 0 ? (
                <li className="px-5 py-6 text-center text-[13px] text-[#9ca3af]">
                  Bu desen/cihaz için selector bulunamadı
                </li>
              ) : null}
            </ol>
          </Card>
        ) : null}

        {/* açıklama — §23.5 renk skalası */}
        <div className="flex flex-wrap items-center gap-6 pb-2">
          {[
            { t: 0.25, label: 'düşük yoğunluk' },
            { t: 0.6, label: 'orta yoğunluk' },
            { t: 1, label: 'en yoğun hücre' },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm" style={{ background: cssColorFor(l.t) }} />
              <span className="text-[12px] text-[#6b7280]">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
